import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

/** ✕ NA orders cancelled in Shopify → Cancelled Orders. Hourly in the background + "Sync now". */
export const NaCancelSyncCard = () => {
  const qc = useQueryClient();
  const { data: s } = useQuery({
    queryKey: ["na-cancel-sync"],
    queryFn: async () => (await api.get("/shopify/na-cancel-sync")).data,
    staleTime: 60_000,
  });
  const run = useMutation({
    mutationFn: async () => (await api.post("/shopify/na-cancel-sync")).data,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["na-cancel-sync"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      const n = r.moved?.length || 0;
      if (r.errors?.length) toast.error(`${r.errors.length} error(s) — see the card`);
      toast.success(n ? `${n} NA order(s) moved to Cancelled Orders: ${r.moved.map((m) => m.po_number).join(", ")}` : `Checked ${r.checked} NA order(s) — none cancelled in Shopify`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Sync failed"),
  });
  if (!s?.configured) return null;
  const last = s.last_run;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="na-cancel-sync-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-2xl">✕</span>
            <h2 className="font-semibold text-lg">NA orders cancelled in Shopify</h2>
            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">Hourly</Badge>
            {s.total_moved ? <Badge className="bg-slate-100 text-slate-700 border border-slate-200" data-testid="na-cancel-sync-total">{s.total_moved} moved so far</Badge> : null}
          </div>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Every hour the CRM asks Shopify about each order in the <b>NA</b> tab. An order that was cancelled in Shopify (customer refunded) moves to <b>Cancelled Orders</b> with Shopify's reason and refund state — no e-mails, no factory refund, nothing goes to stock.
          </p>
          {last && (
            <div className="text-xs text-slate-600 mt-2" data-testid="na-cancel-sync-last">
              Last run {new Date(s.last_run_at).toLocaleString()} · checked {last.checked} · moved {last.moved?.length || 0}
              {last.no_shopify_id ? ` · ${last.no_shopify_id} without Shopify id (leads) skipped` : ""}
              {last.moved?.length ? <div className="font-mono mt-1">{last.moved.map((m) => m.po_number).join(", ")}</div> : null}
              {last.errors?.length ? <div className="text-red-700 mt-1">{last.errors.join(" · ")}</div> : null}
            </div>
          )}
        </div>
        <Button variant="outline" onClick={() => run.mutate()} disabled={run.isPending} data-testid="na-cancel-sync-now">
          {run.isPending ? "Checking Shopify…" : "🔄 Sync now"}
        </Button>
      </div>
    </div>
  );
};
